import { useRef, useCallback, useEffect } from "react";

export function useEditorAutosave(
  save: (html: string) => Promise<void>,
  delay: number = 1200,
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestHtmlRef = useRef<string>("");
  const savedHtmlRef = useRef<string>("");

  const setInitialHtml = useCallback((html: string) => {
    savedHtmlRef.current = html;
    latestHtmlRef.current = html;
  }, []);

  const onUpdate = useCallback(
    (html: string) => {
      latestHtmlRef.current = html;

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        if (latestHtmlRef.current !== savedHtmlRef.current) {
          save(latestHtmlRef.current);
          savedHtmlRef.current = latestHtmlRef.current;
        }
      }, delay);
    },
    [save, delay],
  );

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        if (latestHtmlRef.current !== savedHtmlRef.current) {
          save(latestHtmlRef.current);
        }
      }
    };
  }, [save]);

  return { onUpdate, setInitialHtml };
}
